/*
 * Copyright (C) 2025 SimpliCity Digital Inc - All Rights Reserved
 */

import { noop } from "lodash";
import { forwardRef, useEffect, useState } from "react";
import { useSearchInput } from "../hooks/use-search-input";

import { ReactComponent as CloseIcon } from "cms/assets/icons/x.svg";

/**
 * @param {object} props
 * @param {boolean=} props.autoSearch
 * @param {boolean=} props.navigateOnSubmit
 * @param {SearchEvent=} props.onFocus
 * @param {SearchEvent=} props.onChange
 * @param {Partial<SearchInputClassNames>} props.classNames
 * @param {import('react').ReactNode} props.children
 * @param {import('react').Ref<HTMLInputElement>} ref
 */
export function _SearchInput (props, ref) {
    const [focused, setFocused] = useState(false);
    const autoSearch = props?.autoSearch;
    const navigateOnSubmit = props?.navigateOnSubmit;
    const children = props?.children;
    const onFocus = props?.onFocus ?? noop;
    const onQueryChange = props?.onChange ?? noop;

    const {
        clear,
        onChange,
        onSubmit,
        query,
        search,
    } = useSearchInput(autoSearch, navigateOnSubmit);


    useEffect(
        () => {
            onQueryChange(query);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [query]
    );

    const focus = () => {
        onFocus(); // opens dropdown
        setFocused(true);
    };


    return (
        <form
            onSubmit={onSubmit}
            className={"quick-search-root"}
        >
            <div className={"quick-search-inputContainer"}>
                <input
                    ref={ref}
                    className={"quick-search-input " + (focused ? "search-focused" : "")}
                    value={search}
                    type="text"
                    onChange={onChange}
                    placeholder="Search"
                    aria-label="Search"
                    onFocus={focus}
                    onBlur={() => setFocused(false)}
                />

                {search &&
                    <button
                        type="button"
                        className={"quick-search-reset"}
                        onClick={clear}
                        aria-label="Clear the search query"
                    >
                        <CloseIcon />
                    </button>
                }
            </div>

            <button
                type="submit"
                className={"quick-search-submit topbar-button " +  (focused ? "search-focused" : "")}
                aria-label="Submit the search query"
            >
                {children}
            </button>
        </form>
    );
}

export const QuickSearchInput = forwardRef(_SearchInput);
QuickSearchInput.displayName = "QuickSearchInput";

/**
 * @typedef {() => void} SearchEvent
 * @typedef {string | string[]} ClassName
 */

/**
 * @typedef {object} SearchInputClassNames
 * @property {ClassName} root
 * @property {ClassName} input
 * @property {ClassName} inputContainer
 * @property {ClassName} submit
 * @property {ClassName} reset
 */
